import {
    FlightScheduleAvailabilityStatusEnum,
    IFlightDateScheduleAvailability,
    IFlightsScheduleService
} from "./flights-schedule.service.interface";
import {Station} from "../stations/station.service.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {ServiceBase} from "../service-base";
import {IServiceFactory} from "../service-factory.interface";
import {FlightScheduleCache} from "./flight-schedule-cache";
import {FlightScheduleRange} from "./flight-schedule-range";


export class FlightsScheduleService extends ServiceBase implements IFlightsScheduleService {

    constructor(services: IServiceFactory) {
        super(services);
        this._cache = new FlightScheduleCache(services);
        this._cache.onRouteRemovedFromCache(this._onRouteRemovedFromCache);

        makeObservable<this, '_flightScheduleRanges'>(this, {
            _flightScheduleRanges: observable
        });
    }

    private _flightScheduleRanges: Record<string, FlightScheduleRange[]> = {};
    private readonly _cache: FlightScheduleCache;

    private _onRouteRemovedFromCache = (key: string) => {
        runInAction(() => {
            delete this._flightScheduleRanges[key];
        })
    }

    getDaySchedule(originStation: Station, destinationStation: Station, date: Date): IFlightDateScheduleAvailability {
        const originsAndDestinations = this._makeOriginAndDestinationPairs(originStation, destinationStation);

        const monthlyScheduleStatus = originsAndDestinations.map(pair => this._getScheduleRange(pair[0], pair[1], date).getSchedule(date));

        return {
            status: this._extractFinalStatus(monthlyScheduleStatus),
        }

    }

    async getDayScheduleAsync(originStation: Station, destinationStation: Station, date: Date): Promise<IFlightDateScheduleAvailability> {
        const originsAndDestinations = this._makeOriginAndDestinationPairs(originStation, destinationStation);
        const monthlyScheduleStatus = await Promise.all(originsAndDestinations.map(pair => this._getScheduleRange(pair[0], pair[1], date).getScheduleAsync(date)));
        return {
            status: this._extractFinalStatus(monthlyScheduleStatus),
        }
    }

    private _createNewRange(origin: string, destination: string, date: Date): FlightScheduleRange {
        const firstMonth = this.services.time.monthFromDate(date);
        const lastMonth = this.services.time.monthFromDate(this.services.time.addMonths(date, 2));
        return new FlightScheduleRange(origin, destination, firstMonth.firstDate, lastMonth.lastDate, this._cache, this.services);
    }

    private _getScheduleRange(origin: string, destination: string, date: Date): FlightScheduleRange {
        const routeKey = origin + destination;
        let ranges = this._flightScheduleRanges[routeKey];
        if(!ranges) {
            ranges = [this._createNewRange(origin, destination, date)];
            runInAction(() => {
                this._flightScheduleRanges[routeKey] = ranges;
            })

        }

        let range = ranges.find(r => r.isDateInRange(date));
        if(!range) {
            range = this._createNewRange(origin, destination, date);
            runInAction(() => {
                ranges.push(range!);
            });
        }

        return range;
    }

    private _extractFinalStatus(monthlyScheduleStatus: FlightScheduleAvailabilityStatusEnum[]): FlightScheduleAvailabilityStatusEnum {
        if(monthlyScheduleStatus.some(status => status === FlightScheduleAvailabilityStatusEnum.Available)) {
            return FlightScheduleAvailabilityStatusEnum.Available;
        }

        if(monthlyScheduleStatus.some(status => status === FlightScheduleAvailabilityStatusEnum.Reading)) {
            return FlightScheduleAvailabilityStatusEnum.Reading;
        }

        return FlightScheduleAvailabilityStatusEnum.NotAvailable;

    }

    private _makeOriginAndDestinationPairs(originStation: Station, destinationStation: Station): [string, string][] {
        let origins: string[];
        let destinations: string [];
        if(originStation.allMacStations.length > 0) {
            origins = originStation.allMacStations;
        } else {
            origins = [originStation.stationCode];
        }

        if(destinationStation.allMacStations.length > 0) {
            destinations = destinationStation.allMacStations;
        } else {
            destinations = [destinationStation.stationCode];
        }

        const result: [string, string][] = [];
        for(let origin of origins) {
            for(let destination of destinations) {
                result.push([origin, destination]);
            }
        }
        return result;
    }


}



